import React from 'react';

export default function ArticleHeader() {
  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 leading-tight mt-16 whitespace-nowrap">
            コインランドリー経営｜開業・初期費用・運営・防犯対策まで解説
          </h1>
          <p className="text-lg text-teal-700 font-medium mb-6">
            開業の流れから経営のコツまで成功するコインランドリー事業のすべてを詳しく解説
          </p>

          <div className="bg-teal-800 p-6 rounded-lg text-white">
            <div className="text-xl font-bold mb-4">目次</div>
            <div className="grid grid-cols-2 gap-4 text-left">
              <a href="#section1" onClick={(e) => handleScrollTo(e, 'section1')} className="block hover:underline cursor-pointer">
                1. コインランドリー開業の流れ
              </a>
              <a href="#section2" onClick={(e) => handleScrollTo(e, 'section2')} className="block hover:underline cursor-pointer">
                2. コインランドリー開業に必要な初期費用
              </a>
              <a href="#section3" onClick={(e) => handleScrollTo(e, 'section3')} className="block hover:underline cursor-pointer">
                3. コインランドリーのランニングコスト
              </a>
              <a href="#section4" onClick={(e) => handleScrollTo(e, 'section4')} className="block hover:underline cursor-pointer">
                4. コインランドリー開業のメリット・注意点
              </a>
              <a href="#section5" onClick={(e) => handleScrollTo(e, 'section5')} className="block hover:underline cursor-pointer">
                5. コインランドリーの防犯対策
              </a>
              <a href="#section6" onClick={(e) => handleScrollTo(e, 'section6')} className="block hover:underline cursor-pointer">
                6. まとめ
              </a>
              <a href="#section8" onClick={(e) => handleScrollTo(e, 'section8')} className="block hover:underline cursor-pointer">
                7. コインランドリー経営に関するよくある質問
              </a>
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-8 mb-2 leading-tight">
            コインランドリー経営
          </h2>
          <p className="text-base md:text-lg text-teal-700 font-bold mb-6 leading-snug text-left">
            本記事では、開業の流れ、初期費用やランニングコスト、防犯対策など、コインランドリー経営を始める前に知っておくべき情報を網羅しました。
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mt-8 font-normal text-left">
            　コインランドリー経営は、無人運営が基本のため人件費を大幅に抑えられ、安定した売上と収益が期待できるビジネスモデルです。しかし、コインランドリー開業にあたっては、手順や初期費用、防犯面などに不安を感じる方も少なくありません。本記事では、コインランドリー経営における開業の流れ・初期費用・売上シミュレーション・防犯対策まで、総合的にわかりやすく解説します。
          </p>
        </div>

        <div className="text-center mb-8">
          <img
            src="https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/5647be9c62421fc18e806d9460b60cd9.jpeg"
            alt="コインランドリー経営ガイド - 開業・初期費用・運営・防犯対策"
            className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
            width="800"
            height="320"
            fetchPriority="high"
            decoding="async"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 800px"
          />
        </div>

        <div className="bg-gray-50 p-8 rounded-lg mb-8">
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            ●コインランドリー経営は安定した売上が期待でき、リピーターが多く継続利用が見込める事業です。
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            コインランドリー市場は、2022年時点で約1,000億円規模に達し、急拡大期から安定成長のフェーズへ移行しつつあります。市場が成熟し、生活インフラとして定着してきたことの表れであり、コインランドリー経営の将来性は依然として高いといえます。
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            店舗数が増え続けている背景には、共働き世帯の増加、悪天候の長期化、布団や大型洗濯の需要など、多様化する生活ニーズがあります。「必要な時だけ利用する」というスタイルが広がり、コインランドリーは今まで以上に身近な存在となっています。コインランドリー開業を検討するうえで、こうした需要の拡大は大きな追い風です。
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            また、コインランドリーは利用者のリピート率が非常に高い業態で、一度使い慣れた店舗を継続して利用する傾向が強いため、固定客化しやすく、安定した売上につながりやすい点も大きな特徴です。初期費用を回収した後は、高い収益性を維持しやすいビジネスモデルといえます。
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            このように、コインランドリー業界は「飽和」ではなく、生活に根付き、利便性やサービス品質を求める利用者に支えられながら、今後も安定した成長が期待できる成熟市場です。コインランドリー経営は、適切な立地選定と運営の工夫によって、長期的な売上向上が十分に見込めます。
          </p>
          <p className="text-gray-700 leading-relaxed mb-6 font-normal">
            コインランドリー経営は、いまや安定した土地活用の定番として選ばれるようになっており、コインランドリー開業を検討する方にとって魅力的な選択肢です。共働き家庭の増加や大型洗濯需要の高まりなど、利用ニーズは確実に拡大しています。
          </p>
          <p className="text-gray-700 leading-relaxed font-normal">
            これからコインランドリー経営に参入する場合も、地域特性に合ったサービス設計や清潔感・設備・利便性で差別化を図ることで、長期的に選ばれる店舗づくりが可能です。初期費用の計画から売上目標の設定まで、しっかりと準備することが成功への近道です。
          </p>
        </div>

        <div className="text-center">
          <img
            src="https://static.readdy.ai/image/f1769650ef219847a82938b2ccb51dd4/825f22d430cf2ade3f0e11e15d279355.jpeg"
            alt="コインランドリー開業・経営イメージ"
            className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
            width="800"
            height="320"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 800px"
          />
        </div>
      </div>
    </div>
  );
}
